<template>
  <div
    :id="`log-${log.issued_at}`"
    class="flex min-h-[45px] items-center justify-between rounded-sm bg-blue-50 px-4"
  >
    <div class="space-x-1 text-gray-500">
      <icon-base :icon="IconManualRun" width="12" height="12" />

      <span class="manual-run-text">
        {{ actionText }}
        <template v-if="log.user">
          <strong class="font-medium">
            {{
              props.log.action === DATA_ORCHESTRATION_ACTION_RETRY
                ? $t("ORCHESTRATION.RERAN_BY")
                : $t("ORCHESTRATION.RUN_BY")
            }}
          </strong>
          {{ formatUserFullName(log.user) }}
        </template>
      </span>
    </div>
    <span v-if="log.issued_at" class="manual-run-text text-xs">
      {{ format(new Date(log.issued_at), LONG_LOCALIZED_DATE_TIME) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { format } from "date-fns";
import startCase from "lodash/startCase";
import { storeToRefs } from "pinia";

import {
  DATA_ORCHESTRATION_ACTION_MASTER_STATUS,
  DATA_ORCHESTRATION_ACTION_RETRY,
} from "@/helpers/constants";
import { LONG_LOCALIZED_DATE_TIME } from "@/helpers/constants/dateTime";
import { formatUserFullName } from "@/helpers/formatting";
import useApplicationsStore from "@/stores/applications";

import IconManualRun from "@/components/icons/IconManualRun.vue";

import type { IDataOrchestrationLogEntry } from "@/models/applications";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  log: {
    type: Object as PropType<IDataOrchestrationLogEntry>,
    required: true,
  },
});

const { t } = useI18n();
const { getters } = useStore();
const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const masterStatuses = computed<
  Record<DataOrchestrationMasterStatusType, DataOrchestrationMasterStatus>
>(() => getters["options/dataOrchestrationMasterStatuses"]);

const allTemplateSteps = computed(
  () => activeDataOrchestrationLog.value?.template?.content?.steps || []
);

const actionText = computed(() => {
  const { log } = props;

  if (!log.action) {
    return "";
  }

  if (log.action === DATA_ORCHESTRATION_ACTION_RETRY) {
    const stepRetried = allTemplateSteps.value.find(
      (step) => step.id === log.context?.step_id
    );
    return t("ORCHESTRATION.RETRY_STEP_ACTION", {
      stepName: stepRetried?.title || "",
    });
  }

  if (log.action === DATA_ORCHESTRATION_ACTION_MASTER_STATUS) {
    const contextMasterStatus = log.context?.master_status;
    if (!contextMasterStatus) {
      return "";
    }
    return masterStatuses.value[contextMasterStatus];
  }

  return log.action.split("-").map(startCase).join("-");
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.manual-run-text {
  @apply font-semibold;
}
</style>
